import { useCallback, useReducer } from "react";

export const enum Status {
  idle = "idle",
  pending = "pending",
  resolved = "resolved",
  rejected = "rejected"
}

type State<T = any> = {
  status?: Status;
  data?: T | null;
  error?: Error | null;
};

type Action<T = any> = {
  type: Status;
  data?: T | null;
  error?: Error | null;
};

/**
 * Convert any promise into declarative state
 */
export default function useAsync<T>(initialState?: State<T>) {
  const [{ data, error, status }, dispatch] = useReducer(
    (state: State<T>, action: Action<T>) => {
      // state is unused in this reducer
      switch (action.type) {
        case Status.pending: {
          return { status: Status.pending, data: null, error: null };
        }
        case Status.resolved: {
          return { status: Status.resolved, data: action.data, error: null };
        }
        case Status.rejected: {
          return { status: Status.rejected, data: null, error: action.error };
        }
        default: {
          throw new Error(`Unhandled action type: ${(action as any).type}`);
          return state; // <-- WTF? This needs to be here or it will error in TS
        }
      }
    },
    {
      status: Status.idle,
      data: null,
      error: null,
      ...initialState
    }
  );

  const run = useCallback(
    (promise: Promise<T>) => {
      dispatch({ type: Status.pending });
      promise.then(
        (d) => {
          dispatch({ type: Status.resolved, data: d });
        },
        (e) => {
          dispatch({ type: Status.rejected, error: e });
        }
      );
    },
    [dispatch]
  );

  return { error, status, data, run };
}
